import { useEffect, useMemo } from 'react';
import { throttle } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI } from '../../../trpc/trpc';
import { useRealtimeEvent } from '../../realtime/useRealtimeEvent';

/**
 * The shortest time between two reads of the summary because of events. A render sends
 * `reel.updated` or `kit.asset.updated` up to one time each second (its progress), and the summary
 * does not change with the progress.
 */
export const WATERMARK_SUMMARY_REFRESH_MS = 10 * 1000;

/**
 * The socket invalidates only `fymAPI` tags. This hook reads the tRPC watermark summary again when
 * a kit asset or a reel changed (a new watermarked file, a clean copy) and when posts changed (the
 * count of the posts). The first event reads at once; more events in the next
 * `WATERMARK_SUMMARY_REFRESH_MS` make one read at the end of that time. Mount it one time
 * (`WatermarkUpsellHost`).
 */
export function useWatermarkSummaryRealtime(): void {
  const trpcAPI = useTRPCAPI();
  const queryClient = useQueryClient();
  const refresh = useMemo(
    () =>
      throttle(
        () => {
          queryClient.invalidateQueries({ queryKey: trpcAPI.billing.watermarkSummary.queryKey() }).catch(console.error);
        },
        WATERMARK_SUMMARY_REFRESH_MS,
        { leading: true, trailing: true }
      ),
    [queryClient, trpcAPI]
  );
  useEffect(() => () => refresh.cancel(), [refresh]);
  useRealtimeEvent('kit.asset.updated', refresh);
  useRealtimeEvent('reel.updated', refresh);
  useRealtimeEvent('promo.post.updated', refresh);
  useRealtimeEvent('promo.posts.changed', refresh);
}
